<template>
  <v-text-field
    ref="agTextFieldRef"
    density="compact"
    variant="solo"
    clear-icon="mdi-close"
    class="ag-text-field"
    persistent-hint
    :hide-details="hideDetails"
    :persistent-placeholder="persistentPlaceholder"
    :append-inner-icon="appendIcon"
    :clearable="clearable"
    :disabled="disabled"
    :error-messages="errorMessages"
    :hint="hint"
    :label="localLabel"
    :minlength="minlength"
    :maxlength="maxlength"
    :model-value="localValue"
    :placeholder="placeholder"
    :readonly="readonly"
    :rules="rules"
    :type="type"
    :counter="counter"
    :data-message-color="messageColor"
    v-bind="$attrs"
    @update:model-value="onInput"
    @click:clear="$emit('click:clear')"
    @click:append-inner="$emit('click:append-inner')"
    @keyup="$emit('keyup', $event)"
  >
    <template #append-inner>
      <slot name="append-inner" />
    </template>
    <template v-if="labelDataQa" #label>
      <span :data-qa="normalizeQa(labelDataQa)">{{ localLabel }}</span>
    </template>
  </v-text-field>
</template>

<script>
import { MESSAGE_COLOR } from '@/constants/ConstantMessageColor';
import UtilsString from '@/utils/UtilsString';

export default {
  emits: ['update:modelValue', 'click:clear', 'click:append-inner', 'keyup'],

  props: {
    modelValue: {
      type: [Number, String, Array],
      default: '',
    },

    hint: {
      type: String,
      default: undefined,
    },

    errorMessages: {
      type: String,
      default: undefined,
    },

    label: {
      type: String,
      required: true,
    },

    labelDataQa: {
      type: String,
      default: undefined,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    placeholder: {
      type: String,
    },

    rules: {
      type: Array,
      default: () => [],
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    clearable: {
      type: Boolean,
      default: false,
    },

    appendIcon: {
      type: String,
      default: undefined,
    },

    maxlength: {
      type: [Number, String],
      default: undefined,
    },

    minlength: {
      type: [Number, String],
      default: undefined,
    },

    type: {
      type: String,
      default: 'text',
    },

    persistentPlaceholder: {
      type: Boolean,
      default: false,
    },

    persistentHint: {
      type: Boolean,
      default: false,
    },

    hideDetails: {
      type: [Boolean, String],
      default: 'auto',
    },

    counter: {
      type: [Boolean, Number, String],
      default: false,
    },

    messageColor: {
      type: String,
      default: MESSAGE_COLOR.DEFAULT,
      validator: (val) => Object.values(MESSAGE_COLOR).includes(val),
    },
  },

  computed: {
    localValue: {
      get() {
        return this.modelValue;
      },

      set(value) {
        this.$emit('update:modelValue', value);
      },
    },

    campoObrigatorio() {
      return this.rules.some((rule) => rule?.name?.toLowerCase().includes('required'));
    },

    localLabel() {
      return `${this.label}${this.campoObrigatorio ? ' *' : ''}`;
    },
  },

  methods: {
    normalizeQa: UtilsString.normalizeQa,
    onInput(newVal) {
      let value = newVal;
      if (this.maxlength && newVal?.length > this.maxlength) {
        value = newVal.substring(0, this.maxlength);
      }

      this.$refs.agTextFieldRef.lazyValue = value;
      this.$emit('update:modelValue', value);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/messages.scss';

::v-deep(.v-field--appended) {
  padding-inline-end: 0;
}

.v-input {
  ::v-deep(.v-field) {
    opacity: 1;
    background-color: $neutral-color-low-light;
    border: 1px solid $neutral-color-high-light;
    box-shadow: unset;

    .v-field-label {
      margin: 0 !important;
      opacity: 1;
      color: $brand-color-primary-medium !important;
      font-weight: $font-weight-regular;
      line-height: $line-height-lg;
      letter-spacing: 0.16px;
    }

    &:hover {
      background-color: $brand-color-highlight-light;
    }

    .v-field__field {
      height: 48px;
      padding-left: $spacing-inset-05 !important;

      input {
        height: 100%;
        padding-left: 0;
        padding-bottom: 0;
        padding-top: 16px;
        color: $brand-color-primary-medium !important;

        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        /* Firefox */
        &[type='number'] {
          -moz-appearance: textfield;
        }
      }
    }

    .v-field__append-inner {
      padding-right: $spacing-inset-05;

      i {
        color: $brand-color-primary-medium !important;
        opacity: 1;
      }
    }

    &.v-field--active {
      .v-field-label {
        color: $neutral-color-high-medium !important;
      }
    }

    &.v-field--error {
      border: 2px solid $feedback-color-error-medium;
    }

    &.v-field--disabled {
      opacity: 1;
      border: 0;
      background-color: $neutral-color-low-dark;

      input {
        color: $neutral-color-high-medium !important;
      }

      .v-field-label {
        color: $neutral-color-high-medium !important;
      }

      i {
        color: $neutral-color-high-medium !important;
      }
    }

    .v-field__clearable i {
      opacity: 1;
      color: $brand-color-primary-medium;
    }
  }

  ::v-deep(.v-input__details) .v-counter {
    color: $neutral-color-high-medium;
    min-width: 48px;
    text-align: right;
  }
}
</style>
